import { useAuth0 } from '@auth0/auth0-react';
import { Box, Chip, Tab, Tabs, Typography } from '@mui/material';
import LoadingStateIndicatorProject from 'components/LoadingStateIndicator/LoadingStateIndicatorProject';
import NeurosynthBreadcrumbs from 'components/NeurosynthBreadcrumbs';
import PrivacyToggle from 'components/PrivacyToggle';
import TextEdit from 'components/TextEdit/TextEdit';
import useUserCanEdit from 'hooks/useUserCanEdit';
import {
    useProjectCreatedAt,
    useProjectDescription,
    useProjectIsPublic,
    useProjectMetaAnalysisCanEdit,
    useProjectName,
    useProjectType,
    useProjectUser,
    useProjectUsername,
    useUpdateProjectDescription,
    useUpdateProjectIsPublic,
    useUpdateProjectIsLoading,
    useUpdateProjectName,
} from 'stores/projects/ProjectStore';
import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ProjectReturnTypeEnum } from 'neurosynth-compose-typescript-sdk';

export interface IProjectPageLocationState {
    projectPage?: {
        openCurationDialog?: boolean;
        scrollToMetaAnalysisProceed?: boolean;
    };
}

const ProjectPage = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const { isAuthenticated } = useAuth0();
    const location = useLocation();
    const navigate = useNavigate();

    const updateProjectName = useUpdateProjectName();
    const updateProjectDescription = useUpdateProjectDescription();
    const metaAnalysesTabEnabled = useProjectMetaAnalysisCanEdit();
    const projectName = useProjectName();
    const createdAt = useProjectCreatedAt();
    const projectUser = useProjectUser();
    const projectType = useProjectType();
    const projectUserName = useProjectUsername();
    const projectDescription = useProjectDescription();
    const userCanEdit = useUserCanEdit(projectUser || undefined);
    const isPublic = useProjectIsPublic();
    const updateProjectIsPublic = useUpdateProjectIsPublic();
    const updateProjectIsLoading = useUpdateProjectIsLoading();

    const tab = useMemo(() => {
        if (!metaAnalysesTabEnabled) return 0;
        return location.pathname.includes('meta-analyses') ? 1 : 0;
    }, [location.pathname, metaAnalysesTabEnabled]);

    return (
        <Box sx={{ marginBottom: '5rem' }}>
            <Box sx={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                {isAuthenticated && (
                    <NeurosynthBreadcrumbs
                        breadcrumbItems={[
                            {
                                text: 'Projects',
                                link: '/projects',
                                isCurrentPage: false,
                            },
                            {
                                text: projectName || '',
                                link: '',
                                isCurrentPage: true,
                            },
                        ]}
                    />
                )}
                <LoadingStateIndicatorProject />
                <Box sx={{ marginLeft: 'auto' }}>
                    <PrivacyToggle
                        isPublic={!!isPublic}
                        canEdit={userCanEdit}
                        onChange={updateProjectIsPublic}
                        isLoading={updateProjectIsLoading}
                        tooltipTitle="Toggle project privacy"
                    />
                </Box>
            </Box>

            <Box sx={{ marginBottom: '0.5rem' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box sx={{ marginRight: '1rem', width: '100%' }}>
                        <TextEdit
                            onSave={(updatedName) => updateProjectName(updatedName)}
                            sx={{ width: '100%' }}
                            textFieldSx={{ input: { fontSize: '1.5rem' } }}
                            textToEdit={projectName || ''}
                            editIconIsVisible={userCanEdit}
                        >
                            <Typography
                                sx={{
                                    color: projectName ? 'initial' : 'warning.dark',
                                    display: 'block',
                                }}
                                variant="h5"
                            >
                                {projectName || 'No name'}
                            </Typography>
                        </TextEdit>
                    </Box>
                </Box>
                <TextEdit
                    onSave={(updatedDescription) => updateProjectDescription(updatedDescription)}
                    textFieldSx={{ input: { fontSize: '1.25rem' } }}
                    textToEdit={projectDescription || ''}
                    editIconIsVisible={userCanEdit}
                    multiline
                >
                    <Typography
                        sx={{
                            color: projectDescription ? 'muted.main' : 'warning.dark',
                            whiteSpace: 'pre-line',
                        }}
                        variant="body1"
                    >
                        {projectDescription || 'No description'}
                    </Typography>
                </TextEdit>
                <Box
                    sx={{
                        marginTop: '0.2rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box>
                        <Chip
                            size="small"
                            label={projectType === ProjectReturnTypeEnum.Ibma ? 'IBMA' : 'CBMA'}
                            variant="outlined"
                            color="info"
                            sx={{ marginRight: '0.5rem' }}
                        />
                        {projectUserName && (
                            <Chip
                                size="small"
                                label={`Owner: ${projectUserName}`}
                                variant="outlined"
                                sx={{ marginRight: '0.5rem' }}
                            />
                        )}
                        {createdAt && (
                            <Chip
                                size="small"
                                label={`Created: ${
                                    createdAt.getMonth() + 1
                                }/${createdAt.getDate()}/${createdAt.getFullYear()} ${createdAt.getHours()}:${createdAt.getMinutes()}`}
                                variant="outlined"
                            />
                        )}
                    </Box>
                </Box>
            </Box>

            <Box sx={{ margin: '1.5rem 0' }}>
                <Tabs
                    sx={{
                        '.MuiTabs-flexContainer': {
                            borderBottom: '1px solid lightgray',
                        },
                        '.MuiButtonBase-root.Mui-selected': {
                            backgroundColor: 'white',
                            border: '1px solid',
                            borderTopLeftRadius: '6px',
                            borderTopRightRadius: '6px',
                            borderColor: 'lightgray',
                            borderBottom: '0px',
                            marginBottom: '-2px',
                        },
                        '.MuibuttonBase-root': {},
                        transition: 'none',
                    }}
                    TabIndicatorProps={{
                        sx: {
                            display: 'none',
                        },
                    }}
                    value={tab}
                >
                    <Tab
                        onClick={() => navigate(`/projects/${projectId}/project`)}
                        sx={{
                            color: tab === 0 ? '#ef8a24 !important' : 'primary.main',
                            fontWeight: tab === 0 ? 'bold' : 'normal',
                        }}
                        label="Project"
                    />
                    {metaAnalysesTabEnabled && (
                        <Tab
                            onClick={() => navigate(`/projects/${projectId}/meta-analyses`)}
                            sx={{
                                color: tab === 1 ? '#ef8a24 !important' : 'primary.main',
                                fontWeight: tab === 1 ? 'bold' : 'normal',
                            }}
                            label="Meta-Analyses"
                        />
                    )}
                </Tabs>
            </Box>

            <Outlet />
        </Box>
    );
};

export default ProjectPage;
